import React from 'react'
import {
  PaymentQueueStatus,
  PaymentQueueOrderType,
  IPaymentQueueWithExternalSource,
  ICompany,
  IPaymentQueueLine,
  CreatePayment,
  API,
  IPayment,
} from '@getgreenline/homi-shared'
import { Modal, Tag, Tooltip, Button, Skeleton } from 'antd'
import moment from 'moment'
import {
  capitalizeFirstLetter,
  getCustomerPhoneFromParkedSale,
  parseErrorMsg,
  printHTML,
} from '../../../../utilities/helpers'
import { Link } from 'react-router'
import { Routes } from '../../../../constants/Routes'
import { ParkedSaleStore } from './ParkedSaleStore'
import { ParkedSaleStatusButtonLists } from './ParkedSaleStatusButtonLists'
import { ParkedSaleValidationModal } from './ParkedSaleValidationModal'
import { Observer, inject, observer } from 'mobx-react'
import { PackingSlip } from '../../../../components/PackingSlip'
import { message } from 'antd-v4'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { ParkedSaleBundledDiscount } from './ParkedSaleBundledDiscount'
import { ParkedSaleNonBundledDiscount } from './ParkedSaleNonBundledDiscount'
import { ParkedSaleNonBundleCartDiscountLine } from './ParkedSaleNonBundleCartDiscountLine'
import { BLAZE_COMPANY } from '../../../../constants/general'
import { ReceiptModels, ReceiptUtils } from '@getgreenline/receipts'
import { CustomerReceiptModal } from '../../../CustomerReceiptModal'
import { LoyaltyModels } from '@getgreenline/loyalties'
import { BlazepayEnums, BlazepayPaymentOptionModels } from '@getgreenline/payments'
import { formatPaymentOptionNames, getPaymentOptions } from '../../../../utilities/paymentsHelper'
import { ParkedSaleViewPrintButtons } from './ParkedSaleViewPrintButtons'
import { getUnpaidOrderReceiptDataWithoutReceiptSettings } from '../../../../utilities/receiptHelpers'

interface State {
  isPackingSlipOpen: boolean
  isPaidOrderReceiptModalOpen: boolean
  isUnpaidOrderReceiptModalOpen: boolean
  isValidationModalOpen: boolean
  customerPhone: string
  createPayment?: CreatePayment
  selectedPayment?: IPayment
  loyaltyData?: LoyaltyModels.ILoyaltyData
  paymentOptions?: Map<
    BlazepayEnums.Provider,
    BlazepayPaymentOptionModels.IPaymentOptionEventOrRestData
  >
  unpaidOrderReceiptDataWithoutSettings?: Omit<
    ReceiptModels.IUnpaidOrderReceiptData,
    'receiptSettings'
  >
  isPaidReceiptPrintingRequested: boolean
}
interface Props {
  parkedSaleStore: ParkedSaleStore
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

@inject('currentCompanyStore')
@observer
export class ParkedSaleModal extends React.Component<Props & InjectedProps, State> {
  companyId = this.props.parkedSaleStore.company?.id
  locationId = this.props.parkedSaleStore.currentLocation?.id

  constructor(props: Props & InjectedProps) {
    super(props)
    this.state = {
      isPackingSlipOpen: false,
      isPaidOrderReceiptModalOpen: false,
      isUnpaidOrderReceiptModalOpen: false,
      isValidationModalOpen: false,
      customerPhone: '',
      isPaidReceiptPrintingRequested: false,
    }
  }

  componentDidMount = async () => {
    const { parkedSaleStore, currentCompanyStore } = this.props

    await parkedSaleStore.setProductPairQueue()

    await this.setCustomerPhone(parkedSaleStore.parkedSaleModalQueue, currentCompanyStore?.company)

    await this.convertQueueToPayment()

    if (this.locationId) {
      await this.props.currentCompanyStore?.setCustomReceiptSetting(this.locationId)
    }
  }

  convertQueueToPayment = async () => {
    const { companyId, locationId } = this
    const { parkedSaleStore, currentCompanyStore } = this.props
    const { parkedSaleModalQueue, flatPOSListing } = parkedSaleStore

    if (!companyId || !currentCompanyStore || !currentCompanyStore.productStore) return

    const newPayment = new CreatePayment(companyId, locationId)

    const paymentLines = parkedSaleModalQueue?.paymentLines as IPaymentQueueLine[]

    if (!paymentLines || !parkedSaleModalQueue) return

    const newPaymentQueue: IPaymentQueueWithExternalSource = {
      ...parkedSaleModalQueue,
      paymentLines,
    }

    const productStore = currentCompanyStore.productStore

    const categories = await productStore.getCategories()
    newPayment.setCategories(categories || [])

    const { canUseExternalLoyalty, canUseSalesChannel } = currentCompanyStore
    const canAccessLoyalty = !!currentCompanyStore.company?.permissions.canAccessLoyalty
    const discounts = await productStore.getPOSDiscounts({
      permissions: {
        canUseExternalLoyalty,
        canAccessLoyalty,
      },
      canUseSalesChannel,
    })

    newPayment.setDiscounts(discounts || [])

    await newPayment.populateFromQueue(newPaymentQueue, flatPOSListing)

    const isExternalOrder = !!newPaymentQueue.externalSourceName

    if (newPaymentQueue.completedPayment || isExternalOrder) {
      this.setState({ createPayment: newPayment })
      return
    }

    const paymentWithValidatedDiscounts = productStore.removeInvalidDiscountsFromUnpaidOrder(
      newPayment,
      newPaymentQueue,
    )

    this.setState({ createPayment: paymentWithValidatedDiscounts })
  }

  setCustomerPhone = async (
    parkedSaleModalQueue: IPaymentQueueWithExternalSource | undefined,
    company: ICompany | undefined,
  ) => {
    if (!company || !parkedSaleModalQueue || !parkedSaleModalQueue.customerId) {
      return
    }

    try {
      const customerPhone =
        (await getCustomerPhoneFromParkedSale({ parkedSaleModalQueue, company })) || ''

      this.setState({ customerPhone })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error fetching customer data')
    }
  }

  handleReadyToPrint = () => {
    this.print()
    this.setState({ isPaidReceiptPrintingRequested: false })
  }

  print = () => {
    printHTML('print-section', this.props.parkedSaleStore.company?.name)
  }

  viewReceipt = async () => {
    const { parkedSaleModalQueue, company, currentLocation } = this.props.parkedSaleStore
    if (!parkedSaleModalQueue || !company || !this.state.createPayment) return

    const { currentCompanyStore } = this.props
    if (!currentCompanyStore) return

    try {
      if (!parkedSaleModalQueue.completedPayment) {
        const unpaidOrderReceiptDataWithoutSettings =
          getUnpaidOrderReceiptDataWithoutReceiptSettings({
            companyName: company.name,
            locationName: currentLocation?.name,
            selectedPayment: this.state.createPayment,
            paymentQueue: parkedSaleModalQueue,
          })

        if (!unpaidOrderReceiptDataWithoutSettings) return

        this.setState({
          unpaidOrderReceiptDataWithoutSettings,
          isUnpaidOrderReceiptModalOpen: true,
        })
        return
      }

      const selectedPayment = await API.getPaymentById(
        company.id,
        parkedSaleModalQueue.completedPayment.id,
      )

      let loyaltyData = this.state.loyaltyData

      if (
        currentCompanyStore.company?.permissions.canAccessLoyalty &&
        this.state.loyaltyData === undefined
      ) {
        loyaltyData = await currentCompanyStore.getLoyaltyData(selectedPayment)
      }

      let paymentOptions = this.state.paymentOptions

      if (!paymentOptions) {
        paymentOptions = await getPaymentOptions(company.id, selectedPayment.locationId)
      }

      this.setState({
        selectedPayment,
        loyaltyData,
        paymentOptions,
        isPaidOrderReceiptModalOpen: true,
      })

      return
    } catch (error) {
      console.error(error)
      message.error(parseErrorMsg(error) || 'There was an error fetching the receipt')
    }
  }

  renderCreatePaymentLines = () => {
    const { parkedSaleStore } = this.props
    const { createPayment } = this.state

    if (!createPayment) return null

    const { bundleCartDiscountLines, nonBundleCartDiscountLines, nonBundleProductLines } =
      createPayment

    return (
      <>
        {nonBundleProductLines.map((line, index) => (
          <ParkedSaleNonBundledDiscount
            key={index}
            paymentLine={line}
            parkedSaleStore={parkedSaleStore}
          />
        ))}

        {bundleCartDiscountLines.map((bundle, index) => (
          <ParkedSaleBundledDiscount
            key={index}
            bundledDiscount={bundle}
            parkedSaleStore={parkedSaleStore}
          />
        ))}

        {nonBundleCartDiscountLines.map((line, index) => (
          <ParkedSaleNonBundleCartDiscountLine key={index} discount={line} />
        ))}
      </>
    )
  }

  render = () => {
    const { companyId, renderCreatePaymentLines, print, viewReceipt } = this
    const { parkedSaleStore, currentCompanyStore } = this.props
    const {
      createPayment,
      isPackingSlipOpen,
      isValidationModalOpen,
      customerPhone,
      isPaidOrderReceiptModalOpen,
      isUnpaidOrderReceiptModalOpen,
      selectedPayment,
      loyaltyData,
      paymentOptions,
      unpaidOrderReceiptDataWithoutSettings,
    } = this.state

    const {
      currentLocation,
      isParkedSaleModalOpen,
      setToggleParkedSaleModal,
      parkedSaleModalQueue,
      productLoading,
      setCancelParkedSale,
    } = parkedSaleStore

    const {
      createDate,
      externalSourceHexColor,
      externalSourceName,
      externalOrderId,
      completedPayment,
      orderType,
      customerName,
      notes,
      status,
    } = parkedSaleModalQueue || {}

    const company = currentCompanyStore!.company!
    const {
      id: locationId,
      name: locationName,
      receiptSettings: locationReceiptSettings,
    } = currentLocation!

    const packingSlipData: ReceiptModels.IPackingSlipData = {
      company,
      receiptSettings: locationReceiptSettings,
      payment: parkedSaleModalQueue || createPayment,
      locationName,
      customerPhone: createPayment?.customer?.phone || customerPhone,
    }

    const defaultReceiptSettings = ReceiptUtils.generateDefaultReceiptSettings(company, locationId)

    const receiptSettings =
      currentCompanyStore!.customReceiptSettings.get(locationId) || defaultReceiptSettings

    let unpaidOrderReceiptData: ReceiptModels.IUnpaidOrderReceiptData | undefined = undefined
    if (unpaidOrderReceiptDataWithoutSettings) {
      unpaidOrderReceiptData = {
        ...unpaidOrderReceiptDataWithoutSettings,
        receiptSettings,
      }
    }

    const splitNotes = notes?.split(';')

    const deliveryAddress = splitNotes
      ? splitNotes.length > 1
        ? splitNotes[splitNotes.length - 1].trim()
        : notes //If there's only one note, it's the delivery address (assuming that it is not possible to have a delivery order with no address)
      : ''

    const deliveryNotes = splitNotes
      ? splitNotes.length > 1
        ? splitNotes.slice(0, -1).join(';').trim()
        : '' //If there's only one note, it's the delivery address
      : ''

    return (
      <Observer>
        {() => (
          <>
            <Modal
              className='parked-sales-modal'
              title='Parked sale'
              visible={isParkedSaleModalOpen}
              centered
              footer={<Button onClick={() => setToggleParkedSaleModal(false)}>Close</Button>}
              onCancel={() => setToggleParkedSaleModal(false)}
            >
              <div className='parked-sales-modal-content'>
                {/* Left container */}
                <div className='parked-sales-modal-left-container'>
                  <div className='parked-sales-modal-field'>
                    <p className='parked-sales-modal-field-label'>Date</p>
                    {`${moment(createDate).format('MMMM D, YYYY h:mm a')}`}
                  </div>
                  <div className='parked-sales-modal-field'>
                    <p className='parked-sales-modal-field-label'>Order ID</p>
                    <div className='parked-sales-modal-field-content'>
                      <Tag color={externalSourceHexColor || 'green'}>
                        {externalSourceName || BLAZE_COMPANY}
                      </Tag>
                      <span>{externalOrderId}</span>
                    </div>
                  </div>
                  {completedPayment && companyId && (
                    <div className='parked-sales-modal-field link-to-sales'>
                      <Link
                        to={Routes.DASHBOARD_SALES(companyId, completedPayment.id)}
                        target='_blank'
                      >
                        <Tag color='blue'>Paid</Tag>
                        <p>{completedPayment.customId}</p>
                      </Link>
                    </div>
                  )}
                  <div className='parked-sales-modal-field'>
                    <p className='parked-sales-modal-field-label'>Order type</p>
                    <span
                      style={
                        orderType === PaymentQueueOrderType.PICKUP
                          ? { fontWeight: 400, color: 'var(--gray)' }
                          : { fontWeight: 600, color: 'var(--dark)' }
                      }
                    >
                      {capitalizeFirstLetter(`${orderType}`)}
                    </span>
                  </div>
                  <div className='parked-sales-modal-field'>
                    <p className='parked-sales-modal-field-label'>Customer</p>
                    {customerName}
                  </div>
                </div>
                {/* Right container */}
                <div className='parked-sales-modal-right-container'>
                  {orderType === PaymentQueueOrderType.DELIVERY && (
                    <div className='parked-sales-modal-field'>
                      <p className='parked-sales-modal-field-label'>Delivery Address</p>
                      {deliveryAddress}
                    </div>
                  )}
                  {orderType === PaymentQueueOrderType.DELIVERY ? (
                    <div className='parked-sales-modal-field'>
                      <p className='parked-sales-modal-field-label'>Notes</p>
                      <p>{deliveryNotes}</p>
                    </div>
                  ) : (
                    <div className='parked-sales-modal-field'>
                      <p className='parked-sales-modal-field-label'>Notes</p>
                      <p>{notes}</p>
                    </div>
                  )}
                  <div className='parked-sales-modal-field'>
                    <p className='parked-sales-modal-field-label'>Products</p>
                    <div style={{ maxHeight: 380, overflowY: 'scroll' }}>
                      <div className='parked-sales-items'>
                        {productLoading || !createPayment ? (
                          <Skeleton active />
                        ) : (
                          <>{renderCreatePaymentLines()}</>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='parked-sales-modal-field'>
                    <p className='parked-sales-modal-field-label'>Status</p>
                    <div className='parked-sales-modal-field-content payment-status'>
                      <ParkedSaleStatusButtonLists
                        parkedSaleStore={parkedSaleStore}
                        setValidationModalOpen={(bool) =>
                          this.setState({ isValidationModalOpen: bool })
                        }
                      />

                      {completedPayment && companyId && completedPayment.id && (
                        <Tooltip
                          title={() => (
                            <>
                              Cancelling
                              <Link
                                className='parked-sales-modal-tooltip'
                                to={Routes.DASHBOARD_SALES(
                                  companyId || 0,
                                  completedPayment?.id || '',
                                )}
                                target='_blank'
                              >
                                {completedPayment?.customId}
                              </Link>
                              will cancel this parked sale
                            </>
                          )}
                          placement='left'
                        >
                          <button className='btn-blue-hover' disabled>
                            Cancel
                          </button>
                        </Tooltip>
                      )}

                      {!completedPayment && status !== PaymentQueueStatus.CANCELLED && (
                        <button
                          className='btn-blue-hover'
                          onClick={() => {
                            if (
                              window.confirm('Are you sure you want to cancel the parked sale?')
                            ) {
                              setCancelParkedSale()
                              setToggleParkedSaleModal(false)
                            }
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                  <div className='parked-sales-modal-field'>
                    <ParkedSaleViewPrintButtons
                      label='Packing Slip'
                      viewAction={() => this.setState({ isPackingSlipOpen: true })}
                      printAction={async () => {
                        // Print function works only if there's HTML elements. So the modal should be up to create the invoice.
                        await this.setState({ isPackingSlipOpen: true })
                        print()
                      }}
                    />
                    {currentCompanyStore?.canDisplayOnlineOrdersReceipts && (
                      <ParkedSaleViewPrintButtons
                        label='Receipt'
                        viewAction={viewReceipt}
                        printAction={async () => {
                          this.setState({ isPaidReceiptPrintingRequested: true })
                          await viewReceipt()
                          if (!completedPayment) {
                            this.print()
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </Modal>

            {isPackingSlipOpen && (
              <PackingSlip
                packingSlipData={packingSlipData}
                isPackingSlipOpen={isPackingSlipOpen}
                onCancel={() => this.setState({ isPackingSlipOpen: false })}
                printPackingSlip={print}
                canUseCustomReceipt={currentCompanyStore!.canUseCustomReceipt}
                receiptSettings={receiptSettings}
              />
            )}

            {isPaidOrderReceiptModalOpen && selectedPayment && (
              <CustomerReceiptModal
                payment={selectedPayment}
                onHide={() => this.setState({ isPaidOrderReceiptModalOpen: false })}
                loyaltyData={loyaltyData}
                paymentOptionNames={formatPaymentOptionNames(selectedPayment, paymentOptions)}
                showPaidTag={true}
                onReadyToPrint={
                  this.state.isPaidReceiptPrintingRequested ? this.handleReadyToPrint : undefined
                }
              />
            )}

            {isUnpaidOrderReceiptModalOpen && unpaidOrderReceiptData && (
              <Modal
                title='Receipt'
                centered
                visible={isUnpaidOrderReceiptModalOpen}
                onOk={this.print}
                okText='Print'
                onCancel={() => this.setState({ isUnpaidOrderReceiptModalOpen: false })}
                cancelText='Close'
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: ReceiptUtils.generateUnpaidOrderReceiptHTMLV1(unpaidOrderReceiptData),
                  }}
                />
              </Modal>
            )}

            {isValidationModalOpen && (
              <ParkedSaleValidationModal
                parkedSaleStore={parkedSaleStore}
                isValidationModalOpen={isValidationModalOpen}
                setValidationModalOpen={(bool) => this.setState({ isValidationModalOpen: bool })}
              />
            )}
          </>
        )}
      </Observer>
    )
  }
}
